import React, { memo } from "react";
import { useTranslation } from "react-i18next";

import MetricTrendChart from "../charts/MetricTrendChart";
import ECGHeart from "../icons/ECGHeart";
import LungIcon from "../icons/LungIcon";
import { formatResultsCardTimestamp } from "../../utils/results/recordingIdTime";
import ResultsMetricPickerButton from "./ResultsMetricPickerButton";

function TrendCardSkeleton() {
  return (
    <div className="rounded-2xl bg-card px-3 py-2.5 shadow">
      <div className="space-y-2.5">
        <div className="flex items-center justify-between gap-3">
          <div className="pdx-skeleton-block h-4 w-28 rounded-full" />
          <div className="pdx-skeleton-block h-3 w-20 rounded-full" />
        </div>
        <div className="flex items-stretch gap-3">
          <div className="flex-1 space-y-2">
            <div className="pdx-skeleton-block h-7 w-24 rounded-2xl" />
            <div className="pdx-skeleton-block h-3 w-28 rounded-full" />
          </div>
          <div className="pdx-skeleton-block h-20 w-[42%] rounded-2xl" />
        </div>
      </div>
    </div>
  );
}

function issueText(card, t) {
  if (card.latestIssue === "quality") {
    return t("result.recordingQuality.summary.repeatCardBody", {
      count: 1,
      defaultValue: "Quality check failed",
    });
  }

  return null;
}
function TrendCardIcon({ cardKey, icon }) {
  if (cardKey === "hr") {
    return (
      <span
        className="flex h-[22px] w-[22px] shrink-0 items-center justify-center text-primary [&_svg]:h-[22px] [&_svg]:w-auto [&_svg]:fill-current [&_svg]:stroke-current"
        aria-hidden="true"
      >
        <ECGHeart />
      </span>
    );
  }

  if (cardKey === "rr") {
    return (
      <span
        className="flex h-[22px] w-[22px] shrink-0 items-center justify-center text-primary [&_svg]:h-[22px] [&_svg]:w-auto [&_svg]:fill-current [&_svg]:stroke-current"
        aria-hidden="true"
      >
        <LungIcon />
      </span>
    );
  }

  return (
    <span
      className="material-symbols-outlined text-[22px] leading-none text-primary"
      aria-hidden="true"
    >
      {icon}
    </span>
  );
}

function buildTrendHighlightPoints(card, t) {
  const latestValue = Number(card?.latestDisplayValue);
  const latestTimestampMs = Date.parse(card?.latestTimestamp || "");

  if (!Number.isFinite(latestValue) || !Number.isFinite(latestTimestampMs)) {
    return [];
  }

  return [
    {
      x: latestTimestampMs,
      y: latestValue,
      label: t("results.trends.latestValue", "Latest value"),
    },
  ];
}

function buildTrendTimeframeText(card, xDomainOverride, t) {
  const domainMin = Number(xDomainOverride?.min);
  const domainMax = Number(xDomainOverride?.max);

  if (
    !Number.isFinite(domainMin) ||
    !Number.isFinite(domainMax) ||
    domainMax <= domainMin
  ) {
    return null;
  }

  return t("results.trends.subtitle", "Last 30 days");
}

function TrendCard({ card, xDomainOverride }) {
  const { t, i18n } = useTranslation();
  const locale = i18n?.resolvedLanguage || i18n?.language || "en-GB";
  const takenAt =
    formatResultsCardTimestamp(card?.latestTimestamp, locale) ||
    card.latestDate ||
    card.latestTime ||
    "";
  const displayValueText = card.latestDisplayValueText || card.latestValueText;
  const fallbackValueText = t("results.trends.unavailableValue", "--");
  const resolvedValueText = displayValueText || fallbackValueText;
  const showMutedValue = !card.latestDisplayValueApproved || !displayValueText;
  const timeframeText = buildTrendTimeframeText(card, xDomainOverride, t);
  const emptyTrendXValues =
    xDomainOverride?.min != null && xDomainOverride?.max != null
      ? [xDomainOverride.min, xDomainOverride.max]
      : [];

  const hasTrendValues = card.trend.values.some((value) =>
    Number.isFinite(value),
  );
  const issue = issueText(card, t);
  const highlightPoints = buildTrendHighlightPoints(card, t);

  return (
    <article
      data-testid={`results-trend-card-${card.key}`}
      className="flex min-h-[8.75rem] items-stretch gap-3 rounded-2xl bg-card px-3 py-2.5 shadow text-left"
    >
      <div className="flex min-w-0 flex-1 flex-col justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          <TrendCardIcon cardKey={card.key} icon={card.icon} />
          <div className="min-w-0 flex-1">
            <p className="truncate whitespace-nowrap text-sm font-semibold leading-tight text-typography">
              {card.label}
            </p>
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col justify-end gap-1.5">
          <div className="flex flex-col gap-1">
            <p
              className={`text-[1.75rem] font-semibold leading-none ${
                showMutedValue ? "text-greyscale-3" : "text-typography"
              }`}
            >
              {resolvedValueText}
              {card.unit ? (
                <span
                  className={`ml-1 text-sm font-medium ${
                    showMutedValue ? "" : "text-greyscale-3"
                  }`}
                >
                  {card.unit}
                </span>
              ) : null}
            </p>
            {takenAt ? (
              <p className="muted-text text-xs leading-tight">{takenAt}</p>
            ) : null}
          </div>

          {issue ? (
            <p className="muted-text text-xs leading-tight">{issue}</p>
          ) : null}
        </div>
      </div>

      <div className="flex w-[42%] min-w-[9rem] shrink-0 flex-col gap-1 self-stretch">
        {timeframeText ? (
          <p className="muted-text text-[11px] leading-tight text-right">
            {timeframeText}
          </p>
        ) : null}
        <div className="relative min-h-0 flex-1">
          <div className="absolute inset-0">
            <MetricTrendChart
              height="100%"
              labels={hasTrendValues ? card.trend.labels : []}
              values={
                hasTrendValues
                  ? card.trend.values
                  : emptyTrendXValues.map(() => null)
              }
              xValues={hasTrendValues ? card.trend.xValues : emptyTrendXValues}
              xDomainOverride={xDomainOverride}
              xDomainPaddingPct={0.045}
              timePointMinGapPx={8}
              xMaxTicksLimit={4}
              lineLabel={t("results.trends.dailyAverage", "Daily average")}
              yUnit={card.unit || ""}
              showEmptyPrompt={hasTrendValues}
              showXAxis={false}
              showYAxis={false}
              showYTicks={hasTrendValues}
              showYAxisTitle={false}
              allowPointRemoval={false}
              showMainLine={false}
              showTrendLine={false}
              yDecimals={card.decimals}
              minMax={hasTrendValues ? card.trend.minMax : null}
              counts={hasTrendValues ? card.trend.counts : null}
              highlightPoints={highlightPoints}
              showLegend={false}
            />
          </div>
        </div>
      </div>
    </article>
  );
}

function ResultsTrendCardsSection({
  cards,
  loading,
  error,
  metrics,
  selectedMetricKeys,
  hasCustomSelection,
  onToggleMetric,
  onClearSelection,
  xDomainOverride,
}) {
  const { t } = useTranslation();
  const visibleCards = cards.filter((card) =>
    selectedMetricKeys.includes(card.key),
  );

  return (
    <section className="w-full flex flex-col gap-2 px-4 md:px-0">
      <div className="flex items-start justify-between gap-3">
        <h3 className="subtitle-text text-left">
          {t("results.trends.title", "Summary")}
        </h3>

        <ResultsMetricPickerButton
          buttonLabel={t("results.trends.edit", "Customise")}
          clearLabel={t("results.trends.showAll", "Show all")}
          emptyLabel={t("results.trends.emptyPicker", "No metrics available.")}
          metrics={metrics}
          selectedMetricKeys={selectedMetricKeys}
          hasCustomSelection={hasCustomSelection}
          onToggleMetric={onToggleMetric}
          onClear={onClearSelection}
        />
      </div>

      {loading ? (
        <div className="flex flex-col gap-2">
          <TrendCardSkeleton />
          <TrendCardSkeleton />
        </div>
      ) : null}

      {!loading && error && !cards.length ? (
        <p className="muted-text text-sm">
          {t(
            "results.trends.unavailable",
            "Trend cards are unavailable right now.",
          )}
        </p>
      ) : null}

      {!loading && !error && metrics.length && !visibleCards.length ? (
        <div className="rounded-2xl bg-card px-4 py-4 text-sm text-greyscale-3 shadow">
          {t("results.trends.emptySelection", "No summary cards selected.")}
        </div>
      ) : null}

      {!loading && visibleCards.length ? (
        <div className="flex flex-col gap-2">
          {visibleCards.map((card) => (
            <TrendCard
              key={card.key}
              card={card}
              xDomainOverride={xDomainOverride}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

export default memo(ResultsTrendCardsSection);
